import styled, { css } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { FC, MouseEvent } from "react";
import { DateTime } from "luxon";
import { useQueryClient } from "@tanstack/react-query";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap, Td } from "@/components/Table";
import Text from "@/components/Text";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { ReactComponent as StarIcon } from "@/assets/icons/star-icon.svg";
import { ImageGenerationProjectResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { ImageGenerationProjectStatus } from "@/utils/enums";

const Time = styled(Text)`
  margin-top: 4px;
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  td {
    &:last-child {
      cursor: initial;
    }
  }
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
`;

const StatusDot = styled.div<{ $status?: ImageGenerationProjectStatus }>`
  width: 4px;
  height: 4px;
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 50%;
  margin-right: 6px;
  transform: translateY(1px);
  ${({ $status }) =>
    $status === ImageGenerationProjectStatus.DRAFT &&
    css`
      background: ${({ theme }) => theme.colors.gray};
    `}
  ${({ $status }) =>
    $status === ImageGenerationProjectStatus.PENDING &&
    css`
      background: ${({ theme }) => theme.colors.warning};
    `}
    ${({ $status }) =>
    $status === ImageGenerationProjectStatus.DONE &&
    css`
      background: ${({ theme }) => theme.colors.success};
    `}
    ${({ $status }) =>
    $status === ImageGenerationProjectStatus.FAILED &&
    css`
      background: ${({ theme }) => theme.colors.error};
    `}
`;

const StatusHolder = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  span {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const Statuses = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: nowrap;
  > div {
    flex-shrink: 0;
  }
`;

const StyledActionsDropdown = styled(ActionsDropdown)<{ $wider: boolean }>`
  width: 160px;
  ${({ $wider }) =>
    $wider &&
    css`
      width: 200px;
    `}
`;

const NameHolder = styled.div`
  display: flex;
  align-items: center;
  svg {
    margin-right: 8px;
    flex-shrink: 0;
  }
`;

const PendingMediaCount = styled.span`
  color: ${({ theme }) => theme.colors.gray};
  margin-left: 5px;
`;

interface Props {
  project: ImageGenerationProjectResource;
}

const ImageToolProjectsTableRow: FC<Props> = ({ project }) => {
  const { type } = useParams();

  const navigate = useNavigate();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const renderStatus = (status: ImageGenerationProjectStatus) => {
    switch (status) {
      case ImageGenerationProjectStatus.DRAFT:
        return "Draft";
      case ImageGenerationProjectStatus.PENDING:
        return "Pending";
      case ImageGenerationProjectStatus.DONE:
        return "Done";
      case ImageGenerationProjectStatus.FAILED:
        return "Failed";
      default:
        return "N/A";
    }
  };

  const handleRowClick = (id: number) => {
    navigate(`/general/image-tool/${id}`);
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this project?"
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/image-generation-projects/${project.id}`
      );

      await queryClient.invalidateQueries(["image_generation_projects"], {
        exact: false,
      });

      alerts.success("Project was successfully deleted");
    } catch (e) {
      alerts.error();
    }
  };

  const toggleFavorite = async () => {
    await api.patch<never, { id: number }>(
      `/v1/image-generation-projects/${project.id}/favourite`
    );

    await queryClient.invalidateQueries(["image_generation_projects"], {
      exact: false,
    });
  };

  return (
    <Tr onClick={() => handleRowClick(project.id)}>
      <Td>
        <NameHolder>
          {project.isFavourite && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="16" height="16" rx="8" fill="#FEF7ED" />
              <path
                d="M8.21207 11.3366L10.6369 12.9343C10.9468 13.1386 11.3316 12.8348 11.2396 12.4585L10.5391 9.59241C10.5193 9.51259 10.5217 9.42859 10.5458 9.35008C10.57 9.27157 10.6149 9.20174 10.6755 9.14862L12.8499 7.26645C13.1356 7.01914 12.9881 6.52601 12.6211 6.50123L9.78145 6.30957C9.70496 6.30389 9.6316 6.27572 9.56991 6.22836C9.50822 6.181 9.46074 6.11638 9.43299 6.04204L8.37393 3.26839C8.34515 3.18948 8.29396 3.12155 8.22717 3.07361C8.16038 3.02567 8.08115 3 8 3C7.91885 3 7.83962 3.02567 7.77283 3.07361C7.70604 3.12155 7.65485 3.18948 7.62607 3.26839L6.56701 6.04204C6.53926 6.11638 6.49178 6.181 6.43009 6.22836C6.3684 6.27572 6.29504 6.30389 6.21855 6.30957L3.37894 6.50123C3.01187 6.52601 2.86441 7.01914 3.15011 7.26645L5.3245 9.14862C5.3851 9.20174 5.43005 9.27157 5.45418 9.35008C5.47832 9.42859 5.48065 9.51259 5.46092 9.59241L4.81122 12.2504C4.70085 12.702 5.16264 13.0664 5.53458 12.8214L7.78793 11.3366C7.85131 11.2947 7.92487 11.2724 8 11.2724C8.07513 11.2724 8.14869 11.2947 8.21207 11.3366Z"
                fill="#ED911B"
              />
            </svg>
          )}
          {project.name}
        </NameHolder>
      </Td>
      <Td>
        {DateTime.fromISO(project.createdAt).toLocaleString()} <br />
        <Time $color="gray" $type="extraSmall">
          {DateTime.fromISO(project.createdAt).toFormat("HH:mm")}
        </Time>
      </Td>
      {type !== "automated" && <Td>{project.creator}</Td>}
      <Td>{project.description ? "Text Analysis" : "Custom Search"}</Td>
      <Td>
        <Statuses>
          <div>
            <StatusHolder>
              <span>Stocks:</span>
            </StatusHolder>
            <StatusHolder>
              <span>Getty:</span>
            </StatusHolder>
            <StatusHolder>
              <span>Instagram:</span>
            </StatusHolder>
            <StatusHolder>
              <span>Midjourney:</span>
            </StatusHolder>
          </div>
          <div>
            <StatusHolder>
              <StatusDot $status={project.statuses.stocks} />{" "}
              {renderStatus(project.statuses.stocks)}
            </StatusHolder>
            <StatusHolder>
              <StatusDot $status={project.statuses.getty} />
              {renderStatus(project.statuses.getty)}
            </StatusHolder>
            <StatusHolder>
              <StatusDot $status={project.statuses.instagram} />{" "}
              {renderStatus(project.statuses.instagram)}
            </StatusHolder>
            <StatusHolder>
              <StatusDot $status={project.statuses.midjourney} />{" "}
              {renderStatus(project.statuses.midjourney)}
              {project.statuses.midjourney ===
                ImageGenerationProjectStatus.PENDING && (
                <PendingMediaCount>
                  ({project.midjourneyDoneMediaCount}/
                  {project.midjourneyMediaCount})
                </PendingMediaCount>
              )}
            </StatusHolder>
          </div>
        </Statuses>
      </Td>
      <Td $actionsCol onClick={(e) => e.stopPropagation()}>
        <ActionsWrap>
          <StyledActionsDropdown $wider={project.isFavourite}>
            <ActionsDropdownButton as="button" onClick={toggleFavorite}>
              <span>
                <StarIcon />
              </span>
              {project.isFavourite ? "Remove from" : "Add to"} Favorites
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </StyledActionsDropdown>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default ImageToolProjectsTableRow;
